import React from 'react';
import './LiveBar.css';
import { Card, Skeleton, Tooltip, Typography, theme } from 'antd';
import { PhoneOutlined, TeamOutlined } from '@ant-design/icons';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { formatNumber } from '@leedpulse-monorepo/utils';
import type { LiveStats } from '../../../../domain/use_cases/leedflow/overview/getLiveStats';

const { useToken } = theme;

interface LiveBarProps {
  stats: LiveStats;
  status: ContextStatus;
}

// Le seul bloc de la page qui ne dépend pas du sélecteur de dates : il dit
// l'instant présent. Rafraîchi toutes les 30 s par `useLeedflowLiveStats`.
const LiveBar: React.FC<LiveBarProps> = ({ stats, status }) => {
  const { token } = useToken();

  if (status === ContextStatus.ERROR) return null;

  const items = [
    {
      key: 'calls',
      icon: <PhoneOutlined />,
      value: formatNumber(stats.callsInProgress),
      label: 'appels VoIP en cours',
      // Dire ce que le chiffre ne couvre pas : un appel GSM en cours est
      // indistinguable d'un document fantôme tant que LEED-1717 n'est pas
      // corrigé. Sans cette note, on lirait « tous les appels ».
      hint: "La GSM n'est pas mesurable ici : un appel GSM en cours ne se distingue pas d'un document jamais résolu (LEED-1717).",
    },
    {
      key: 'lines',
      icon: <TeamOutlined />,
      value: formatNumber(stats.activeLines),
      label: 'lignes actives',
      hint: `Lignes ayant eu un appel ou un SMS dans les ${stats.windowMinutes} dernières minutes. Des lignes et non des utilisateurs : un appel GSM ne porte aucun identifiant d'utilisateur.`,
    },
  ];

  return (
    <Card size="small">
      {status === ContextStatus.LOADING ? (
        <Skeleton active paragraph={false} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontWeight: 600 }}>
            <span className="live-bar__pulse" style={{ color: token.colorSuccess }} />
            Maintenant
          </span>

          {items.map((item) => (
            <Tooltip key={item.key} title={item.hint}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: token.colorTextSecondary }}>{item.icon}</span>
                <span style={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {item.value}
                </span>
                <Typography.Text type="secondary">{item.label}</Typography.Text>
              </span>
            </Tooltip>
          ))}
        </div>
      )}
    </Card>
  );
};

export default LiveBar;
